import {
  Link as ChakraLink,
  type LinkProps as ChakraLinkProps,
} from '@chakra-ui/react';
import { useLocation } from 'react-router';

import { RouterLink, type RouterLinkProps } from '../RouterLink';

export type LinkProps = Omit<ChakraLinkProps, 'href'> & RouterLinkProps;

/**
 * Themeable Link which wraps {@link RouterLink} (react-router's Link component,
 * now with added `disabled`).
 *
 * @example
 * ```tsx
 * <Link to="/">anchor text</Link>
 * ```
 *
 * ### External links
 *
 * Note: Our default usage pattern is to link to a client-side route using
 * react-router; in the case you want to link to an external URL, instead.
 *
 * > Using target="_blank" without rel="noreferrer" and rel="noopener"
 * > makes the website vulnerable to `window.opener` API exploitation
 * > attacks, although note that, in newer browser versions setting
 * > `target="_blank"` implicitly provides the same protection as setting
 * > `rel="noopener"`.
 *
 * @see
 * https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements/a#security_and_privacy
 *
 * @example
 * ```tsx
 * import { Link } from '@chakra-ui/react';
 *
 * <Link href="https://example.com">External</Link>
 * <Link href="https://example.com" target="_blank" rel="noreferrer noopener">New tab</Link>
 * ```
 *
 * ### Styling the active link
 *
 * > Use the `_currentPage` condition to style active links when using
 * > `aria-current="page"`
 *
 * @see https://chakra-ui.com/docs/components/link#styling-active-links
 *
 * @example
 * ```tsx
 * <Link _currentPage={{ color: 'blue.500' }}>Anchor</Link>
 * ```
 *
 * @see https://reactrouter.com/7.13.1/api/components/Link
 */
export const Link = (props: LinkProps) => {
  const { pathname } = useLocation();
  const isActive = pathname === props.to;
  return (
    <ChakraLink asChild>
      <RouterLink aria-current={isActive ? 'page' : undefined} {...props} />
    </ChakraLink>
  );
};
